<script lang="ts" setup>
import { Button } from '@forgd/ui-kit/button'
import { closeSlideover, NAV_EXPAND_MIN_WIDTH_PX } from '@forgd/ui-kit/blocks/nav'
import { useMutationObserver, useWindowSize } from '@vueuse/core'

interface Options {
  title?: string
  icon?: string
  cta?: string
  contact?: boolean
  containerClass?: false | string
  containerLayout?: 'center' | 'fluid'
  collapsedNav?: boolean
  appFooterClass?: string
}

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<Options>(), { containerClass: 'max-w-[1200px]', containerLayout: 'center' })
const options = computed<Options>(() => {
  return { ...props, ...useRoute().meta }
})

const auth = useAuth()

useHead({
  title: options.value.title,
})

const slots = useSlots()

const expanded = useState('nav:expanded', () => false)
const hasSidebarSlot = ref(false)
// make sure menu is closed between route changes
const route = useRoute()
watch(route, () => {
  closeSlideover()
}, {
  immediate: true,
})
const { width } = useWindowSize()
const shouldExpandMenuFromWidth = computed(() => width.value > NAV_EXPAND_MIN_WIDTH_PX)
hasSidebarSlot.value = !!slots.sidebar
watch([shouldExpandMenuFromWidth, hasSidebarSlot, () => options.value.collapsedNav], () => {
  if (hasSidebarSlot.value || options.value.collapsedNav) {
    expanded.value = false
    return
  }
  expanded.value = shouldExpandMenuFromWidth.value
}, {
  immediate: true,
})

const sidebarContainer = useTemplateRef('sidebarContainer')
// we can't watch slots so we need to watch the DOM
useMutationObserver(sidebarContainer, () => {
  hasSidebarSlot.value = !!sidebarContainer.value?.children.length
}, {
  childList: true,
})
</script>

<template>
  <div data-layout="core-dashboard">
    <div
      class="bg-page text-primal min-h-screen font-normal font-display flex flex-col"
    >
      <div class="flex min-w-0 grow relative">
        <ClientOnly>
          <slot name="nav" />
          <template #fallback>
            <div class="hidden md:block w-12.5 shrink-0 border-r border-gray-700 bg-brand-900" aria-hidden="true" />
          </template>
        </ClientOnly>
        <main class="flex min-w-0 grow flex-col min-h-0" data-inertable>
          <slot name="mobile-header" />
          <!-- page header -->
          <div
            class="h-20 px-5 hidden md:flex relative justify-between items-center border-b border-default"
          >
            <slot name="page-header">
              <slot name="header">
                <div class="flex gap-2 items-center">
                  <img v-if="options.icon && options.icon.endsWith('.svg')" :src="options.icon" class="h-[32px]">
                  <div class="text-sm font-semibold shrink-0">
                    {{ options.title }}
                  </div>
                </div>
              </slot>

              <CoreSearchButton class="absolute left-1/2 transform -translate-x-1/2" />

              <div class="grow-0 flex items-center gap-3">
                <div class="flex gap-2 items-center">
                  <slot name="header-actions" />
                  <slot name="header-notifications" />
                  <ClientOnly>
                    <slot name="header-auth">
                      <div v-if="auth.isLoggedIn" class="flex gap-2 items-center">
                        <CoreHeaderProfile />
                      </div>
                      <div v-else class="flex gap-2 items-center">
                        <Button as-child variant="outline" size="sm">
                          <NuxtLink to="/login">
                            Login
                          </NuxtLink>
                        </Button>
                        <Button as-child size="sm">
                          <NuxtLink to="/register">
                            Join Forgd
                          </NuxtLink>
                        </Button>
                      </div>
                    </slot>
                  </ClientOnly>
                </div>
              </div>
            </slot>
          </div>

          <div class="min-h-[calc(100vh-80px)] flex w-full min-w-0">
            <div ref="sidebarContainer">
              <slot v-if="$slots.sidebar" name="sidebar" />
            </div>
            <div class="flex min-w-0 grow flex-col">
              <div class="flex min-w-0 grow">
                <div
                  class="w-full grow min-w-0"
                  :class="options.containerLayout === 'fluid' ? 'flex min-h-0 flex-col' : ''"
                >
                  <slot name="subheader" />
                  <div
                    class="flex flex-col grow"
                    :class="[
                      options.containerLayout === 'center' ? ['mx-auto p-5 pb-12 space-y-10', options.containerClass] : 'w-full min-h-0',
                    ]"
                  >
                    <slot />
                  </div>
                </div>
                <slot v-if="$slots['right-sidebar']" name="right-sidebar" />
              </div>
              <slot name="footer" />
            </div>
          </div>
        </main>
      </div>

      <footer class="w-full min-w-0 overflow-x-clip" :class="options.appFooterClass">
        <slot name="app-footer" />
        <CoreFooter />
      </footer>
    </div>
  </div>
</template>
